
import React from 'react';
import { RefreshCw, Wifi, WifiOff, AlertTriangle } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { useThemeConfig } from '@/components/ui/theme-config-provider';

interface EnhancedLoadingStateProps {
  isLoading?: boolean;
  isOnline?: boolean;
  hasError?: boolean;
  errorMessage?: string;
  skeletonCount?: number;
  showRetryButton?: boolean;
  onRetry?: () => void;
  loadingMessage?: string;
  emptyStateMessage?: string;
  emptyStateAction?: {
    label: string;
    onClick: () => void;
  };
  className?: string;
  'aria-label'?: string;
}

export const EnhancedLoadingState: React.FC<EnhancedLoadingStateProps> = ({
  isLoading = false,
  isOnline = true,
  hasError = false,
  errorMessage = 'Si è verificato un errore',
  skeletonCount = 3,
  showRetryButton = true,
  onRetry,
  loadingMessage = 'Caricamento in corso...',
  emptyStateMessage = 'Nessun elemento trovato',
  emptyStateAction,
  className = '',
  'aria-label': ariaLabel
}) => {
  const { theme } = useThemeConfig();
  // Stato di errore
  if (hasError) {
    return (
      <div
        className={`flex flex-col items-center justify-center p-8 text-center space-y-4 ${className}`}
        role="alert"
        aria-label={ariaLabel || 'Errore di caricamento'}
      >
        <AlertTriangle
          className="w-12 h-12"
          aria-hidden="true"
          style={{ color: theme.errorColor }}
        />
        <div>
          <h3
            className="text-lg font-medium mb-2"
            style={{ color: theme.errorColor }}
          >
            Errore di caricamento
          </h3>
          <p className="text-sm mb-4" style={{ color: theme.sectionTextColor }}>
            {errorMessage}
          </p>
          {showRetryButton && onRetry && (
            <Button
              onClick={onRetry}
              variant="outline"
              style={{ color: theme.errorColor, borderColor: theme.errorColor }}
              aria-label="Riprova a caricare i dati"
            >
              <RefreshCw className="w-4 h-4 mr-2" aria-hidden="true" />
              Riprova
            </Button>
          )}
        </div>
      </div>
    );
  }

  // Stato di caricamento
  if (isLoading) {
    return (
      <div 
        className={`space-y-4 ${className}`}
        role="status"
        aria-label={ariaLabel || loadingMessage}
        aria-live="polite"
      >
        <div
          className="flex items-center gap-2 p-3 rounded-lg"
          style={{ backgroundColor: `${theme.primaryColor}20` }}
        >
          <RefreshCw
            className="w-4 h-4 animate-spin"
            aria-hidden="true"
            style={{ color: theme.primaryColor }}
          />
          <span
            className="text-sm font-medium"
            style={{ color: theme.primaryColor }}
          >
            {loadingMessage}
          </span>
          {!isOnline && (
            <div className="ml-auto flex items-center gap-1" style={{ color: theme.warningColor }}>
              <WifiOff className="w-4 h-4" aria-hidden="true" />
              <span className="text-xs">Offline</span>
            </div>
          )}
        </div>
        
        {/* Skeleton loaders */}
        <div className="space-y-3">
          {Array.from({ length: skeletonCount }, (_, i) => (
            <div key={i} className="p-4 border rounded-lg">
              <div className="flex items-center gap-3 mb-3">
                <Skeleton className="h-8 w-8 rounded-full" />
                <div className="space-y-2 flex-1">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
                <Skeleton className="h-6 w-16 rounded-full" />
              </div>
              <div className="space-y-2">
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-2/3" />
              </div>
            </div>
          ))}
        </div>
        
        <span className="sr-only">{loadingMessage}</span>
      </div>
    );
  }

  // Stato vuoto
  return (
    <div
      className={`flex flex-col items-center justify-center p-12 text-center space-y-4 ${className}`}
      role="status"
      aria-label={ariaLabel || emptyStateMessage}
    >
      <div
        className="w-16 h-16 rounded-full flex items-center justify-center mb-4"
        style={{ backgroundColor: `${theme.primaryColor}20` }}
      >
        <div
          className="w-8 h-8 rounded"
          aria-hidden="true"
          style={{ backgroundColor: `${theme.primaryColor}40` }}
        />
      </div>
      
      <div>
        <h3
          className="text-lg font-medium mb-2"
          style={{ color: theme.sectionTextColor }}
        >
          {emptyStateMessage}
        </h3>
        
        {!isOnline && (
          <div
            className="flex items-center justify-center gap-2 mb-4"
            style={{ color: theme.warningColor }}
          >
            <WifiOff className="w-4 h-4" aria-hidden="true" />
            <span className="text-sm">Modalità offline attiva</span>
          </div>
        )}
        
        {emptyStateAction && (
          <Button
            onClick={emptyStateAction.onClick}
            className="mt-4"
            aria-label={`${emptyStateAction.label} - Azione principale`}
          >
            {emptyStateAction.label}
          </Button>
        )}
      </div>
    </div>
  );
};

export default EnhancedLoadingState;
